export default {
  en: {
    title: "Models",
    subtitle: "Connect model services, choose which models agents use, and decide what happens when one is busy or unavailable.",
    registryVersion: "Registry v{{v}}",
    names: {
      "qwen2-5-0-5b": "Qwen 2.5 small (0.5B)",
      "qwen2-5-1-5b": "Qwen 2.5 (1.5B)",
      "qwen2-5-7b": "Qwen 2.5 (7B)",
      "qwen3-4b": "Qwen3 (4B), stronger private model",
      "bge-m3": "Search model (BGE-M3)",
      "chat-default": "Standard chat",
      "chat-local-strong": "Stronger private chat",
      "chat-small": "Fast chat (small)",
      "qwen-local": "Qwen on your own servers",
      "guardrails-checker": "Guardrail checker",
      "decide-default": "Decision helper",
      "embed-default": "Search model",
      "docintel-chat": "Document reader",
      "docintel-vision": "Scanned document reader",
      "speech-to-text": "Listening (speech to text)",
      "text-to-speech": "Speaking (text to speech)",
      "whisper-small-local": "Whisper small, on our servers",
      "piper-ar-local": "Arabic voice, on our servers",
      "piper-en-local": "English voice, on our servers",
      "groq-whisper-large-v3-turbo": "Whisper large turbo (Groq, online)",
      "groq-whisper-large-v3": "Whisper large (Groq, online)",
      "mistral-voxtral-mini": "Voxtral listening (Mistral, online)",
      "mistral-voxtral-tts": "Voxtral voice (Mistral, online)",
      "speech-local": "Speech server (our servers)",
      ollama: "Your own servers (local models)",
      vllm: "Your own servers (local models)",
    },
    tab: { providers: "Providers", models: "Models", aliases: "Model choices", fallbacks: "Fallbacks", routes: "Routing rules", budgets: "Spending limits", cache: "Saved answers" },
    actions: "Actions",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    env: { dev: "dev", test: "test", staging: "final check", prod: "prod" },
    classification: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
    providers: {
      secretsNote: "Provider keys live in OpenBao. Only references are shown here; values are never sent to the browser.",
      name: "Provider",
      kind: "Type",
      hosting: "Hosting",
      hostings: { local: "In cluster", online: "Online (egress allow-list)" },
      endpoint: "Endpoint",
      key: "Key reference",
      keyTooltip: "OpenBao reference; the gateway reads the value at runtime",
      noKey: "No key",
      kinds: { vllm: "vLLM", ollama: "Ollama (dev only)", openai_compatible: "OpenAI-compatible", openai: "OpenAI", anthropic: "Anthropic", azure: "Azure OpenAI", bedrock: "AWS Bedrock", google: "Google", mistral: "Mistral", deepseek: "DeepSeek", cohere: "Cohere", groq: "Groq", elevenlabs: "ElevenLabs (voice)" },
      add: "Add a connection",
      editTitle: "Change connection",
      edit: "Change connection {{name}}",
      delete: "Delete connection {{name}}",
      deleteConfirm: "Delete this connection? Models that use it must be removed first.",
      saved: "Connection {{name}} saved",
      deleted: "Connection {{name}} deleted",
      endpointHelp: "Always explicit. Online providers need https and an egress allow-list entry.",
      secretRefHelp: "OpenBao reference path#key only. Never paste the key itself.",
      timeout: "Timeout (seconds)",
    },
    models: {
      id: "Model",
      provider: "Provider",
      context: "Context window",
      classification: "Max classification",
      evalIn: "Eval ({{env}})",
      statusNote: "A model is ready for a stage only after it passes its checks and a person approves it. Changing a model here keeps that approval.",
      add: "Add a model",
      editTitle: "Change model",
      edit: "Change model {{name}}",
      delete: "Delete model {{name}}",
      deleteConfirm: "Delete this model? Model choices and backups that use it must be changed first.",
      saved: "Model {{name}} saved",
      deleted: "Model {{name}} deleted",
      providerModelId: "Provider model ID",
      version: "Pinned version",
      versionHelp: "Revision or digest of the weights",
      capabilities: "Capabilities",
      maxOutput: "Max output tokens",
      priceIn: "Price in (per 1K tokens)",
      priceOut: "Price out (per 1K tokens)",
      caps: { chat: "Chat", embeddings: "Embeddings", rerank: "Rerank", asr: "Speech to text", tts: "Text to speech", vision: "Vision", ocr: "OCR", tools: "Tool calls", json_schema: "JSON schema", streaming: "Streaming" },
    },
    eval: { registered: "Registered", evaluating: "Evaluating", approved: "Approved", rejected: "Rejected", deprecated: "Deprecated" },
    aliases: {
      alias: "Model choice",
      description: "Description",
      targets: "Targets (traffic weight)",
      weight: "weight {{w}}",
      dark: "dark (no traffic)",
      add: "Add a model choice",
      editTitle: "Change model choice",
      edit: "Change model choice {{name}}",
      delete: "Delete model choice {{name}}",
      deleteConfirm: "Delete this model choice? Agents and routing rules that use it must be changed first.",
      saved: "Model choice {{name}} saved",
      deleted: "Model choice {{name}} deleted",
      targetModel: "Model {{n}}",
      targetWeight: "Share of requests for model {{n}}",
      removeTarget: "Remove target {{n}}",
      addTarget: "Add target",
      weightHelp: "Weight 0 keeps a model registered without traffic (dark launch).",
    },
    fallbacks: {
      rule: "A fallback pair is only allowed between models whose evals are approved in that stage.",
      add: "Add a backup",
      name: "Name",
      environment: "Stage",
      primary: "Primary model",
      fallback: "Fallback model",
      onlyApproved: "Models without approved evals in this stage cannot be selected.",
      saved: "Backup saved",
      delete: "Delete backup {{name}}",
      deleteConfirm: "Delete this backup?",
      editTitle: "Change backup",
      edit: "Change backup {{name}}",
      deleted: "Backup deleted",
      priority: "Priority",
    },
    issues: {
      name_invalid: "Name must be lowercase letters, digits, '.', '_' or '-'.",
      primary_required: "Choose a primary model.",
      fallback_required: "Choose a fallback model.",
      primary_not_approved: "The primary model has no approved evals in this stage.",
      fallback_not_approved: "The fallback model has no approved evals in this stage.",
      same_model: "A model cannot fall back to itself.",
      id_invalid: "Use letters, digits, '.', '_', ':', '@' or '-' (max 128), starting with a letter or digit.",
      base_url_invalid: "Enter an absolute http(s) URL.",
      kind_online_only: "This provider type is always hosted online.",
      online_needs_https: "Online providers must use https.",
      secret_ref_invalid: "Enter an OpenBao reference in the form path#key.",
      option_secret: "Secrets belong in OpenBao, not in options.",
      timeout_invalid: "Timeout must be greater than 0.",
      provider_unknown: "Choose a registered provider.",
      provider_model_id_required: "Enter the provider's model ID.",
      version_required: "Enter the pinned version.",
      context_window_invalid: "Context window must be a positive whole number.",
      max_output_invalid: "Max output tokens must be a positive whole number.",
      price_invalid: "Prices must be decimal numbers of 0 or more.",
      targets_required: "Add at least one target.",
      target_unknown: "Every target must be a registered model.",
      weight_invalid: "Weights must be whole numbers of 0 or more.",
      no_positive_weight: "At least one target needs a weight above 0.",
      priority_invalid: "Priority must be a whole number.",
      route_alias_unknown: "Route to an existing model choice.",
      match_alias_unknown: "Match only existing model choices or *.",
      tenant_required: "Enter the organisation.",
      limit_required: "Set a max cost, max tokens or both.",
      max_tokens_invalid: "Max tokens must be a positive whole number.",
      max_cost_invalid: "Max cost must be a number above 0.",
      subject_forbidden: "Organisation spending limits have no subject.",
      subject_required: "Agent and project spending limits need a subject.",
      ttl_invalid: "TTLs must be whole seconds of at least 1.",
      threshold_invalid: "Threshold must be above 0 and at most 1.",
      semantic_alias_required: "Saving answers by meaning needs an existing model choice for search.",
      semantic_alias_not_embedding: "The model choice for saved answers by meaning must point to embedding models.",
      semantic_alias_not_local: "Saving answers by meaning must use a local model.",
      shares_total: "The traffic shares must add up to 100%.",
    },
    routes: {
      priority: "Priority",
      name: "Rule",
      match: "When",
      routeTo: "Routes to",
      strategy: "Strategy",
      strategies: { weighted: "Weighted", cheapest: "Cheapest", fastest: "Fastest" },
      rule: "The first matching rule (lowest priority number) rewrites the model choice or picks a strategy.",
      add: "Add a routing rule",
      editTitle: "Change routing rule",
      edit: "Change routing rule {{name}}",
      delete: "Delete routing rule {{name}}",
      deleteConfirm: "Delete this routing rule?",
      saved: "Routing rule saved",
      deleted: "Routing rule {{name}} deleted",
      priorityHelp: "Lower runs first",
      matchAliases: "Requested model choices",
      anyAlias: "* matches any model choice",
      matchTasks: "Tasks",
      matchTenants: "Organisations",
      listHelp: "Type a value and press Enter. Empty matches all.",
      minClassification: "Minimum classification",
    },
    budgets: {
      id: "Spending limit",
      scope: "Scope",
      scopes: { tenant: "Organisation", agent: "Agent", project: "Project" },
      period: "Period",
      periods: { day: "Daily", month: "Monthly" },
      maxCost: "Max cost",
      maxTokens: "Max tokens",
      add: "Add spending limit",
      editTitle: "Edit spending limit",
      edit: "Edit spending limit {{name}}",
      delete: "Delete spending limit {{name}}",
      deleteConfirm: "Delete this spending limit?",
      saved: "Spending limit {{name}} saved",
      deleted: "Spending limit {{name}} deleted",
      tenant: "Organisation",
      subject: "Subject (agent or project)",
      limitHelp: "Set max cost, max tokens or both",
    },
    cache: {
      note: "Answers above the highest data level are never saved. Saving answers by meaning must use a local model.",
      exact: "Saved answers, word for word",
      exactTtl: "Exact TTL (seconds)",
      maxClassification: "Max cached classification",
      semantic: "Saved answers by meaning",
      semanticAlias: "Model choice for search",
      threshold: "Similarity threshold",
      semanticTtl: "Semantic TTL (seconds)",
      saved: "Saved answers settings saved",
    },
  },
  ar: {
    title: "النماذج",
    subtitle: "اربط خدمات النماذج، واختر النماذج التي يستخدمها الوكلاء، وحدد ما يحدث عندما يكون نموذج مشغولًا أو غير متاح.",
    registryVersion: "السجل الإصدار {{v}}",
    names: {
      "qwen2-5-0-5b": "Qwen 2.5 صغير (0.5B)",
      "qwen2-5-1-5b": "Qwen 2.5 (1.5B)",
      "qwen2-5-7b": "Qwen 2.5 (7B)",
      "qwen3-4b": "Qwen3 (4B)، النموذج الخاص الأقوى",
      "bge-m3": "نموذج البحث (BGE-M3)",
      "chat-default": "المحادثة القياسية",
      "chat-local-strong": "المحادثة الخاصة الأقوى",
      "chat-small": "محادثة سريعة (صغيرة)",
      "qwen-local": "Qwen على خوادمكم",
      "guardrails-checker": "مدقق الضوابط الوقائية",
      "decide-default": "مساعد القرارات",
      "embed-default": "نموذج البحث",
      "docintel-chat": "قارئ المستندات",
      "docintel-vision": "قارئ المستندات الممسوحة",
      "speech-to-text": "الاستماع (من كلام إلى نص)",
      "text-to-speech": "التحدث (من نص إلى كلام)",
      "whisper-small-local": "Whisper الصغير على خوادمنا",
      "piper-ar-local": "صوت عربي على خوادمنا",
      "piper-en-local": "صوت إنجليزي على خوادمنا",
      "groq-whisper-large-v3-turbo": "Whisper الكبير السريع (Groq، عبر الإنترنت)",
      "groq-whisper-large-v3": "Whisper الكبير (Groq، عبر الإنترنت)",
      "mistral-voxtral-mini": "الاستماع Voxtral (Mistral، عبر الإنترنت)",
      "mistral-voxtral-tts": "صوت Voxtral (Mistral، عبر الإنترنت)",
      "speech-local": "خادم الكلام (خوادمنا)",
      ollama: "خوادمكم (نماذج محلية)",
      vllm: "خوادمكم (نماذج محلية)",
    },
    tab: { providers: "المزوّدون", models: "النماذج", aliases: "خيارات النماذج", fallbacks: "البدائل", routes: "قواعد التوجيه", budgets: "حدود الإنفاق", cache: "الإجابات المحفوظة" },
    actions: "الإجراءات",
    save: "حفظ",
    cancel: "إلغاء",
    delete: "حذف",
    env: { dev: "التطوير", test: "الاختبار", staging: "التجريبية", prod: "التشغيل الفعلي" },
    classification: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيد" },
    providers: {
      secretsNote: "تُحفظ مفاتيح المزوّدين في OpenBao. تُعرض المراجع فقط ولا تُرسل القيم إلى المتصفح أبدًا.",
      name: "المزوّد",
      kind: "النوع",
      hosting: "الاستضافة",
      hostings: { local: "داخل العنقود", online: "عبر الإنترنت (قائمة سماح)" },
      endpoint: "نقطة الاتصال",
      key: "مرجع المفتاح",
      keyTooltip: "مرجع OpenBao؛ تقرأ البوابة القيمة وقت التشغيل",
      noKey: "بدون مفتاح",
      kinds: { vllm: "vLLM", ollama: "Ollama (للتطوير فقط)", openai_compatible: "متوافق مع OpenAI", openai: "OpenAI", anthropic: "Anthropic", azure: "Azure OpenAI", bedrock: "AWS Bedrock", google: "Google", mistral: "Mistral", deepseek: "DeepSeek", cohere: "Cohere", groq: "Groq", elevenlabs: "ElevenLabs (صوت)" },
      add: "إضافة اتصال",
      editTitle: "تغيير الاتصال",
      edit: "تغيير الاتصال {{name}}",
      delete: "حذف الاتصال {{name}}",
      deleteConfirm: "حذف هذا الاتصال؟ يجب حذف النماذج التي تستخدمه أولًا.",
      saved: "حُفظ الاتصال {{name}}",
      deleted: "حُذف الاتصال {{name}}",
      endpointHelp: "يُحدَّد دائمًا صراحةً. يحتاج المزوّد عبر الإنترنت إلى https وإدراج في قائمة السماح.",
      secretRefHelp: "مرجع OpenBao بصيغة path#key فقط. لا تُلصق المفتاح نفسه أبدًا.",
      timeout: "المهلة (بالثواني)",
    },
    models: {
      id: "النموذج",
      provider: "المزوّد",
      context: "نافذة السياق",
      classification: "أعلى تصنيف",
      evalIn: "التقييم ({{env}})",
      statusNote: "يصبح النموذج جاهزًا لمرحلة ما فقط بعد اجتيازه الفحوص واعتماده من شخص. تغيير النموذج هنا يحافظ على هذا الاعتماد.",
      add: "إضافة نموذج",
      editTitle: "تعديل النموذج",
      edit: "تعديل النموذج {{name}}",
      delete: "حذف النموذج {{name}}",
      deleteConfirm: "حذف هذا النموذج؟ يجب تعديل خيارات النماذج والبدائل التي تستخدمه أولًا.",
      saved: "تم حفظ النموذج {{name}}",
      deleted: "تم حذف النموذج {{name}}",
      providerModelId: "معرّف النموذج لدى المزوّد",
      version: "الإصدار المثبّت",
      versionHelp: "مراجعة الأوزان أو بصمتها",
      capabilities: "القدرات",
      maxOutput: "أقصى رموز للإخراج",
      priceIn: "سعر الإدخال (لكل ألف رمز)",
      priceOut: "سعر الإخراج (لكل ألف رمز)",
      caps: { chat: "محادثة", embeddings: "تضمينات", rerank: "إعادة ترتيب", asr: "تحويل الكلام إلى نص", tts: "تحويل النص إلى كلام", vision: "رؤية", ocr: "التعرف الضوئي", tools: "استدعاء الأدوات", json_schema: "مخطط JSON", streaming: "بث" },
    },
    eval: { registered: "مسجل", evaluating: "قيد التقييم", approved: "معتمد", rejected: "مرفوض", deprecated: "متقادم" },
    aliases: {
      alias: "خيار النموذج",
      description: "الوصف",
      targets: "الأهداف (وزن الحركة)",
      weight: "الوزن {{w}}",
      dark: "خامل (بلا حركة)",
      add: "إضافة خيار نموذج",
      editTitle: "تغيير خيار النموذج",
      edit: "تغيير خيار النموذج {{name}}",
      delete: "حذف خيار النموذج {{name}}",
      deleteConfirm: "حذف خيار النموذج هذا؟ يجب تغيير الوكلاء وقواعد التوجيه التي تستخدمه أولًا.",
      saved: "حُفظ خيار النموذج {{name}}",
      deleted: "حُذف خيار النموذج {{name}}",
      targetModel: "النموذج {{n}}",
      targetWeight: "نصيب الطلبات للنموذج {{n}}",
      removeTarget: "إزالة الهدف {{n}}",
      addTarget: "إضافة هدف",
      weightHelp: "الوزن 0 يبقي النموذج مسجلًا دون حركة (إطلاق خامل).",
    },
    fallbacks: {
      rule: "لا يُسمح بزوج بديل إلا بين نماذج معتمدة التقييم في تلك المرحلة.",
      add: "إضافة بديل",
      name: "الاسم",
      environment: "المرحلة",
      primary: "النموذج الأساسي",
      fallback: "النموذج البديل",
      onlyApproved: "لا يمكن اختيار نماذج بلا تقييم معتمد في هذه المرحلة.",
      saved: "حُفظ البديل",
      delete: "حذف البديل {{name}}",
      deleteConfirm: "حذف هذا البديل؟",
      editTitle: "تعديل البديل",
      edit: "تعديل البديل {{name}}",
      deleted: "حُذف البديل",
      priority: "الأولوية",
    },
    issues: {
      name_invalid: "يجب أن يتكون الاسم من أحرف لاتينية صغيرة وأرقام و'.' أو '_' أو '-'.",
      primary_required: "اختر نموذجًا أساسيًا.",
      fallback_required: "اختر نموذجًا بديلًا.",
      primary_not_approved: "النموذج الأساسي ليس لديه تقييم معتمد في هذه المرحلة.",
      fallback_not_approved: "النموذج البديل ليس لديه تقييم معتمد في هذه المرحلة.",
      same_model: "لا يمكن أن يكون النموذج بديلًا لنفسه.",
      id_invalid: "استخدم أحرفًا لاتينية وأرقامًا و'.' أو '_' أو ':' أو '@' أو '-' (حتى 128)، تبدأ بحرف أو رقم.",
      base_url_invalid: "أدخل عنوان http(s) كاملًا.",
      kind_online_only: "هذا النوع من المزوّدين مستضاف عبر الإنترنت دائمًا.",
      online_needs_https: "يجب أن يستخدم المزوّد عبر الإنترنت https.",
      secret_ref_invalid: "أدخل مرجع OpenBao بصيغة path#key.",
      option_secret: "تُحفظ الأسرار في OpenBao وليس في الخيارات.",
      timeout_invalid: "يجب أن تكون المهلة أكبر من 0.",
      provider_unknown: "اختر مزوّدًا مسجلًا.",
      provider_model_id_required: "أدخل معرّف النموذج لدى المزوّد.",
      version_required: "أدخل الإصدار المثبّت.",
      context_window_invalid: "يجب أن تكون نافذة السياق عددًا صحيحًا موجبًا.",
      max_output_invalid: "يجب أن يكون أقصى رموز الإخراج عددًا صحيحًا موجبًا.",
      price_invalid: "يجب أن تكون الأسعار أعدادًا عشرية لا تقل عن 0.",
      targets_required: "أضف هدفًا واحدًا على الأقل.",
      target_unknown: "يجب أن يكون كل هدف نموذجًا مسجلًا.",
      weight_invalid: "يجب أن تكون الأوزان أعدادًا صحيحة لا تقل عن 0.",
      no_positive_weight: "يحتاج هدف واحد على الأقل إلى وزن أكبر من 0.",
      priority_invalid: "يجب أن تكون الأولوية عددًا صحيحًا.",
      route_alias_unknown: "وجّه إلى خيار نموذج موجود.",
      match_alias_unknown: "طابِق أسماء مستعارة موجودة فقط أو *.",
      tenant_required: "أدخل الجهة.",
      limit_required: "حدد أقصى تكلفة أو أقصى عدد رموز أو كليهما.",
      max_tokens_invalid: "يجب أن يكون أقصى عدد رموز عددًا صحيحًا موجبًا.",
      max_cost_invalid: "يجب أن تكون أقصى تكلفة عددًا أكبر من 0.",
      subject_forbidden: "حدود إنفاق الجهة ليس لها موضوع.",
      subject_required: "تحتاج حدود إنفاق الوكيل والمشروع إلى موضوع.",
      ttl_invalid: "يجب أن تكون مدد الصلاحية ثواني صحيحة لا تقل عن 1.",
      threshold_invalid: "يجب أن تكون العتبة أكبر من 0 ولا تتجاوز 1.",
      semantic_alias_required: "يحتاج التخزين الدلالي إلى خيار نموذج للتضمين موجود.",
      semantic_alias_not_embedding: "يجب أن يشير خيار النموذج للتخزين الدلالي إلى نماذج تضمين.",
      semantic_alias_not_local: "يجب أن تستخدم تضمينات التخزين الدلالي نموذجًا محليًا.",
      shares_total: "يجب أن يكون مجموع أنصبة الطلبات 100%.",
    },
    routes: {
      priority: "الأولوية",
      name: "القاعدة",
      match: "عندما",
      routeTo: "يوجَّه إلى",
      strategy: "الاستراتيجية",
      strategies: { weighted: "موزون", cheapest: "الأرخص", fastest: "الأسرع" },
      rule: "أول قاعدة مطابقة (أصغر رقم أولوية) تعيد كتابة خيار النموذج أو تختار استراتيجية.",
      add: "إضافة قاعدة توجيه",
      editTitle: "تعديل قاعدة التوجيه",
      edit: "تعديل قاعدة التوجيه {{name}}",
      delete: "حذف قاعدة التوجيه {{name}}",
      deleteConfirm: "حذف قاعدة التوجيه هذه؟",
      saved: "حُفظت قاعدة التوجيه",
      deleted: "تم حذف قاعدة التوجيه {{name}}",
      priorityHelp: "الأصغر يُطبَّق أولًا",
      matchAliases: "خيارات النماذج المطلوبة",
      anyAlias: "* يطابق أي خيار نموذج",
      matchTasks: "المهام",
      matchTenants: "الجهات",
      listHelp: "اكتب قيمة واضغط Enter. الفراغ يطابق الكل.",
      minClassification: "أدنى تصنيف",
    },
    budgets: {
      id: "حد الإنفاق",
      scope: "النطاق",
      scopes: { tenant: "المؤسسة", agent: "الوكيل", project: "المشروع" },
      period: "الفترة",
      periods: { day: "يومية", month: "شهرية" },
      maxCost: "أقصى تكلفة",
      maxTokens: "أقصى عدد رموز",
      add: "إضافة حد إنفاق",
      editTitle: "تعديل حد الإنفاق",
      edit: "تعديل حد الإنفاق {{name}}",
      delete: "حذف حد الإنفاق {{name}}",
      deleteConfirm: "حذف هذا الحد؟",
      saved: "تم حفظ حد الإنفاق {{name}}",
      deleted: "تم حذف حد الإنفاق {{name}}",
      tenant: "الجهة",
      subject: "الموضوع (وكيل أو مشروع)",
      limitHelp: "حدد أقصى تكلفة أو أقصى عدد رموز أو كليهما",
    },
    cache: {
      note: "لا تُخزَّن الردود التي يتجاوز تصنيفها الحد الأقصى. يجب أن تعمل تضمينات التخزين الدلالي على نموذج محلي.",
      exact: "التخزين المطابق",
      exactTtl: "مدة صلاحية المطابق (ثوانٍ)",
      maxClassification: "أعلى تصنيف قابل للتخزين",
      semantic: "التخزين الدلالي",
      semanticAlias: "خيار النموذج للتضمين",
      threshold: "عتبة التشابه",
      semanticTtl: "مدة صلاحية الدلالي (ثوانٍ)",
      saved: "تم حفظ إعدادات الإجابات المحفوظة",
    },
  },
};
